import React from "react";
import { Dialog } from "../../components/Dialogs/Dialog";
import {
  Box,
  Grid,
  SelectChangeEvent,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import {
  CustomFormLabel,
  CustomTextField,
} from "../../components/formsComponents";
import produce from "immer";
import { FileUpload } from "../../components/FileUpload/components/FileUpload";
import {
  HeaderType,
  MediaType,
} from "../../redux/whatsappTemplate/whatsappTemplate.types";
import { config } from "../../config";

interface IVaraiblesSamplesDialogProps {
  open: boolean;
  content: string;
  varaibles: string[];
  mediaType?: MediaType;
  headerType?: HeaderType;
  mediaFiles?: any[];
  onClose: () => void;
  onSave: (finalList: string[], files: any[]) => void;
}

export const VaraiblesSampleDialog: React.FC<IVaraiblesSamplesDialogProps> = ({
  open,
  content,
  varaibles,
  mediaType = "IMAGE",
  headerType = "TEXT",
  mediaFiles = [],
  onClose,
  onSave,
}) => {
  const theme = useTheme();
  const regex = /\{\{.*?\}\}/g; // Matches {{number}} pattern
  const foundMatches = content.match(regex) || [];
  const [state, setState] = React.useState<{ value: string }[]>([]);
  const [prevContent, setPrevContent] = React.useState(content);
  const [uploadedFile, setUploadedFile] = React.useState<any>(null);

  const getVarIndex = (match: string) => Number(match.replace(/[{}]/g, "")) - 1;

  React.useEffect(() => {
    if (headerType === "MEDIA" && mediaFiles && mediaFiles.length > 0) {
      // Only take the first file if multiple are provided
      setUploadedFile(mediaFiles[0]);
    }
  }, [content, varaibles, headerType, mediaFiles]);

  const handleSubmit = () => {
    onSave(
      state.map((x) => x.value),
      uploadedFile ? [uploadedFile] : [],
    );
  };

  const handleFileChange = (file: File | null) => {
    if (file) {
      setUploadedFile({ file, name: file.name });
    } else {
      setUploadedFile(null);
    }
  };

  const handleVariableChange =
    (index: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;

      setState((prevState) =>
        produce(prevState, (draftState) => {
          draftState[index] = { value };
        }),
      );

      setPrevContent((prevContent) => {
        let preview = content;
        const currentValues = state.map((x, i) =>
          i === index ? value : x.value,
        );

        foundMatches.forEach((match) => {
          const varIndex = getVarIndex(match);
          const v = currentValues[varIndex] || "";
          preview = preview.replace(match, v || match);
        });

        return preview;
      });
    };

  React.useEffect(() => {
    const finalList: { value: string }[] = [];
    if (varaibles.length === 0) {
      setState(foundMatches.map((x) => ({ value: "" })));
      setPrevContent(content);
      return;
    }
    let data: { [key: number]: string } = {};
    for (let i = 0; i < varaibles.length; i++) {
      data[i] = varaibles[i];
    }
    for (let i = 0; i < foundMatches.length; i++) {
      if (data[i]) {
        finalList.push({ value: data[i] });
      } else {
        finalList.push({ value: "" });
      }
    }
    setState(finalList);

    let preview = content;
    foundMatches.forEach((match) => {
      const varIndex = getVarIndex(match);
      const value = data[varIndex] || "";
      preview = preview.replace(match, value || match);
    });

    setPrevContent(preview);
  }, [content, varaibles]);

  return (
    <Dialog
      open={open}
      title={"Samples"}
      onClose={onClose}
      size="md"
      actions={[
        {
          type: "button",
          label: "Close",
          variant: "text",
          onClick: onClose,
        },
        {
          type: "button",
          label: "Save",
          variant: "contained",
          onClick: handleSubmit,
        },
      ]}
    >
      <Grid container spacing={1}>
        <Grid item xs={12} md={6}>
          {headerType === "MEDIA" && (
            <Box mb={2}>
              <CustomFormLabel>Upload File</CustomFormLabel>
              <FileUpload
                value={uploadedFile?.name || ""}
                accept={
                  mediaType === "IMAGE" ? "image/*" : ".pdf,.doc,.docx,.txt"
                }
                onChange={handleFileChange}
                onDelete={() => {
                  setUploadedFile(null);
                }}
              />
            </Box>
          )}

          {state.map((item, index) => {
            return (
              <React.Fragment key={index}>
                <CustomFormLabel>{`Variable ${index + 1}`}</CustomFormLabel>
                <CustomTextField
                  fullWidth
                  value={item.value}
                  onChange={handleVariableChange(index)}
                />
              </React.Fragment>
            );
          })}
        </Grid>
        <Grid item xs={12} md={6}>
          <Box
            p={2}
            borderRadius={2}
            sx={{
              backgroundColor: theme.palette.primary.main,
              minHeight: "200px",
              color: "#fff",
            }}
          >
            <Typography variant="h3" fontWeight={600}>
              Preview
            </Typography>

            {headerType === "MEDIA" && uploadedFile && (
              <Stack spacing={1} mt={2} direction={"column"}>
                {uploadedFile.file ? (
                  <Box>
                    {mediaType === "IMAGE" ? (
                      <img
                        src={URL.createObjectURL(uploadedFile.file)}
                        alt={uploadedFile.name}
                        style={{
                          maxWidth: "100%",
                        }}
                      />
                    ) : (
                      <iframe
                        src={URL.createObjectURL(uploadedFile.file)}
                        style={{
                          width: "100%",
                          border: "none",
                        }}
                        title={uploadedFile.name}
                      />
                    )}
                  </Box>
                ) : uploadedFile.path ? (
                  <Box>
                    {mediaType === "IMAGE" ? (
                      <img
                        src={`${config.api.baseUrl}/general/get-signed-url?key=${uploadedFile.path}`}
                        alt={uploadedFile.path}
                        style={{
                          maxWidth: "100%",
                        }}
                      />
                    ) : (
                      <iframe
                        src={`${config.api.baseUrl}/general/get-signed-url?key=${uploadedFile.path}`}
                        style={{
                          width: "100%",
                          border: "none",
                        }}
                        title={uploadedFile.path}
                      />
                    )}
                  </Box>
                ) : null}
              </Stack>
            )}

            <Stack spacing={1} direction={"column"}>
              <Typography
                variant="body1"
                fontSize={"1rem"}
                sx={{
                  p: {
                    margin: 0,
                  },
                }}
              >
                <span
                  dangerouslySetInnerHTML={{
                    __html: prevContent,
                  }}
                />
              </Typography>
            </Stack>
          </Box>
        </Grid>
      </Grid>
    </Dialog>
  );
};
